import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserRound, Link2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { inr } from "@/lib/brand";
import { Switch } from "@/components/ui/switch";
import { CarFormDialog } from "@/components/admin/CarFormDialog";
import { HostLinkDialog } from "@/components/admin/HostLinkDialog";
import { ZoomcarImportDialog } from "@/components/admin/ZoomcarImportDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminCars() {
  const api = useApi();
  const [cars, setCars] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [hostCar, setHostCar] = useState(null);
  const [draft, setDraft] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/cars").then((r) => setCars(r.data));
  }, [api]);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (c) => {
    const { id, name, host_email, host_share_pct, ...body } = c;
    try { await api.put(`/admin/cars/${id}`, { ...body, active: !c.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (c) => {
    try { await api.delete(`/admin/cars/${c.id}`); toast.success("Car deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-cars">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Cars <span className="text-neutral-500">({cars.length})</span></h1>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="admin-import-zoomcar" className="flex items-center gap-2 rounded-md border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-[#FF5A00]"><Link2 className="h-4 w-4" />Import from Zoomcar</button>
          <button onClick={() => { setEdit(null); setDraft(null); setOpen(true); }} data-testid="admin-add-car" className="flex items-center gap-2 rounded-md bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" />Add car</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#141414]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>{["Car", "Type", "Specs", "Price/hr", "Owner", "Listed", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {cars.map((c) => (
              <tr key={c.id} data-testid={`admin-car-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={c.images.front} alt="" className="h-12 w-18 w-[72px] rounded object-cover" />
                    <div><p className="font-medium">{c.name}</p><p className="text-xs text-neutral-500">{c.variant} · {c.year} · {c.city}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3">{c.type}</td>
                <td className="px-4 py-3 text-neutral-400">{c.transmission} · {c.fuel} · {c.seats} seats</td>
                <td className="px-4 py-3">{inr(c.price_per_hour)}/hr</td>
                <td className="px-4 py-3 text-xs" data-testid={`admin-car-host-${c.id}`}>{c.host_email ? <>{c.host_email}<p className="text-neutral-500">{c.host_share_pct}% share</p></> : <span className="text-neutral-400">Hoorecars</span>}</td>
                <td className="px-4 py-3"><Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`admin-car-active-${c.id}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setHostCar(c)} title="Car owner" data-testid={`admin-car-hostlink-${c.id}`} className="rounded-md p-2 hover:bg-white/10"><UserRound className="h-4 w-4" /></button>
                    <button onClick={() => { setEdit(c); setDraft(null); setOpen(true); }} data-testid={`admin-car-edit-${c.id}`} className="rounded-md p-2 hover:bg-white/10"><Pencil className="h-4 w-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid={`admin-car-delete-${c.id}`} className="rounded-md p-2 text-rose-400 hover:bg-white/10"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle><AlertDialogDescription>This removes the car permanently. To hide it temporarily, switch off "Listed" instead.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction onClick={() => remove(c)} data-testid="admin-car-delete-confirm" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CarFormDialog open={open} onOpenChange={setOpen} car={edit} draft={draft} onSaved={load} />
      <ZoomcarImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={(d) => { setEdit(null); setDraft(d); setOpen(true); }} />
      <HostLinkDialog car={hostCar} onOpenChange={(v) => !v && setHostCar(null)} onSaved={load} />
    </div>
  );
}
